import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface Receipt {
  receiptId: string;
  orderNumber: string;
  orderDate: number;
  dateFormatted: string;
  store: string;
  lineItemCount: number;
  totalNetFormatted: string;
  totalNet: number;
}

interface ReceiptsResponse {
  receipts: Receipt[];
  count: number;
}

interface MonthlyData {
  date: number;
  timestamp: number;
  month: string;
  spent: number;
}

interface PriceHistoryItem {
  date: number;
  dateFormatted: string;
  unitPrice: number;
  unitPriceFormatted: string;
  quantity: number;
}

interface Product {
  productName: string;
  history: PriceHistoryItem[];
  minPrice: string;
  maxPrice: string;
  avgPrice: string;
}

interface PriceHistoryResponse {
  products: Product[];
  totalProducts: number;
}

// Custom formatter for time axis labels
const formatTimeAxis = (timestamp: number) => {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
};

export default function DashboardPage() {
  const { userId } = useAuth();

  const [data, setData] = useState<ReceiptsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;

    const fetchReceipts = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await apiClient.get(`/api/receipts`);
        setData(response.data);
      } catch (err) {
        console.error('Failed to fetch receipts:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchReceipts();
  }, [userId]);

  // Calculate statistics from receipts
  const calculateStats = () => {
    if (!data || data.receipts.length === 0) {
      return {
        totalSpent: '£0.00',
        receiptCount: 0,
        averageBasket: '£0.00',
        thisMonthSpent: '£0.00',
      };
    }

    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Calculate one year ago
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    let totalSpent = 0;
    let thisMonthSpent = 0;
    let lastYearSpent = 0;
    let lastYearReceiptCount = 0;

    data.receipts.forEach((receipt) => {
      const receiptDate = new Date(receipt.orderDate * 1000);
      const receiptAmount = receipt.totalNet || 0;
      totalSpent += receiptAmount;

      // Check if receipt is from current month
      if (receiptDate.getMonth() === currentMonth && receiptDate.getFullYear() === currentYear) {
        thisMonthSpent += receiptAmount;
      }

      // Check if receipt is from last year
      if (receiptDate >= oneYearAgo) {
        lastYearSpent += receiptAmount;
        lastYearReceiptCount += 1;
      }
    });

    // Average basket should only look back over the last year
    const averageBasket = lastYearReceiptCount > 0 ? lastYearSpent / lastYearReceiptCount : 0;

    return {
      totalSpent: `£${(totalSpent / 100).toFixed(2)}`,
      receiptCount: data.receipts.length,
      averageBasket: `£${(averageBasket / 100).toFixed(2)}`,
      thisMonthSpent: `£${(thisMonthSpent / 100).toFixed(2)}`,
    };
  };

  // Calculate monthly spend data with proper timestamps
  const calculateMonthlyData = (): MonthlyData[] => {
    if (!data || data.receipts.length === 0) {
      return [];
    }

    const monthlyMap = new Map<number, number>();

    data.receipts.forEach((receipt) => {
      const receiptDate = new Date(receipt.orderDate * 1000);
      // Get first day of month at midnight UTC
      const monthStart = new Date(receiptDate.getFullYear(), receiptDate.getMonth(), 1);
      const monthKey = monthStart.getTime();

      const currentAmount = monthlyMap.get(monthKey) || 0;
      monthlyMap.set(monthKey, currentAmount + (receipt.totalNet || 0));
    });

    // Convert to array and sort by date
    const monthlyArray = Array.from(monthlyMap, ([timestamp, spent]) => {
      const date = new Date(timestamp);
      const monthKey = date.toLocaleDateString('en-GB', { year: 'numeric', month: 'short' });
      return {
        date: timestamp,
        timestamp,
        month: monthKey,
        spent: Math.round(spent / 100 * 100) / 100, // Convert pence to pounds
      };
    });

    return monthlyArray.sort((a, b) => a.timestamp - b.timestamp);
  };

  // Fetch price history data
  const [priceHistory, setPriceHistory] = useState<PriceHistoryResponse | null>(null);
  const [priceHistoryLoading, setPriceHistoryLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;

    const fetchPriceHistory = async () => {
      try {
        setPriceHistoryLoading(true);
        
        console.log('🔍 Fetching price history');
        const response = await apiClient.get(`/api/price-history`);
        console.log('✅ Price history response:', response.data);
        setPriceHistory(response.data);
      } catch (err) {
        console.error('❌ Failed to fetch price history:', err);
        // Don't set error state, just skip the chart
      } finally {
        setPriceHistoryLoading(false);
      }
    };

    fetchPriceHistory();
  }, [userId]);

  // Get top products by price change count
  const getTopPriceTrendProducts = (limit: number = 5) => {
    if (!priceHistory || priceHistory.products.length === 0) {
      console.log('⚠️  No price history data available');
      return [];
    }

    console.log(`📊 Total products from API: ${priceHistory.products.length}`);
    
    // Filter products with at least 2 data points
    const productsWithHistory = priceHistory.products
      .filter(p => {
        const hasEnoughHistory = p.history.length >= 2;
        if (!hasEnoughHistory) {
          console.log(`  ⏭️  Skipping "${p.productName}" - only ${p.history.length} history point(s)`);
        }
        return hasEnoughHistory;
      })
      .sort((a, b) => b.history.length - a.history.length)
      .slice(0, limit);

    console.log(`✅ Found ${productsWithHistory.length} products with enough history (>= 2 points)`, productsWithHistory.map(p => `${p.productName} (${p.history.length} points)`));

    return productsWithHistory;
  };

  // Format price history data for chart display
  const formatPriceHistoryForChart = (products: Product[]) => {
    if (products.length === 0) {
      return [];
    }

    // Collect all unique dates
    const allDates = new Set<number>();
    products.forEach(product => {
      product.history.forEach(item => {
        allDates.add(item.date);
      });
    });

    // Sort dates
    const sortedDates = Array.from(allDates).sort((a, b) => a - b);

    // Create data points for each date
    return sortedDates.map(date => {
      const dataPoint: any = {
        date,
        dateFormatted: new Date(date * 1000).toLocaleDateString('en-GB'),
      };

      products.forEach(product => {
        const priceItem = product.history.find(h => h.date === date);
        if (priceItem) {
          dataPoint[product.productName] = priceItem.unitPrice / 100; // Convert pence to pounds
        }
      });

      return dataPoint;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-700">
        <h2 className="text-lg font-bold mb-2">Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  const stats = calculateStats();
  const monthlyData = calculateMonthlyData();

  return (
    <div className="page-shell">
      <div className="flex flex-col gap-3">
        <h1 className="text-3xl font-semibold text-ink-700">Dashboard</h1>
        <p className="text-sm text-ink-500 max-w-2xl">
          Track how your grocery spending evolves month-over-month and spotlight the products driving price changes.
        </p>
      </div>

      <section className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        <div className="card p-6">
          <div className="flex items-start justify-between">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wide text-ink-400">Total Spent</span>
              <p className="mt-3 text-3xl font-semibold text-ink-700">{stats.totalSpent}</p>
            </div>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600 text-lg font-bold">
              £
            </span>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-start justify-between">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wide text-ink-400">Receipts</span>
              <p className="mt-3 text-3xl font-semibold text-ink-700">{stats.receiptCount}</p>
              <p className="text-xs text-ink-400 mt-1">Captured in the last 12 months</p>
            </div>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-ink-100 text-ink-500 text-lg">🧾</span>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-start justify-between">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wide text-ink-400">Average Basket</span>
              <p className="mt-3 text-3xl font-semibold text-ink-700">{stats.averageBasket}</p>
              <p className="text-xs text-ink-400 mt-1">Per trip over the last year</p>
            </div>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 text-accent-600 text-lg">🛒</span>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-start justify-between">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wide text-ink-400">This Month</span>
              <p className="mt-3 text-3xl font-semibold text-ink-700">{stats.thisMonthSpent}</p>
            </div>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-50 text-primary-600 text-lg">📆</span>
          </div>
        </div>
      </section>

      <section className="card p-6 space-y-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-ink-700">Monthly Spend</h2>
            <p className="text-sm text-ink-500">Rolling total of your orders grouped by calendar month.</p>
          </div>
        </div>
        {monthlyData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="timestamp"
                type="number"
                scale="time"
                domain={['dataMin', 'dataMax']}
                tickFormatter={formatTimeAxis}
                style={{ fontSize: '12px' }}
              />
              <YAxis label={{ value: 'Spent (£)', angle: -90, position: 'insideLeft' }} tickFormatter={(value: number) => `£${value.toFixed(0)}`} />
              <Tooltip
                formatter={(value: any) => `£${value.toFixed(2)}`}
                labelFormatter={(label: any) => formatTimeAxis(label)}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="spent"
                stroke="#2563eb"
                strokeWidth={2.5}
                dot={false}
                isAnimationActive={false}
                name="Monthly Spend"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="rounded-xl border border-dashed border-ink-200/80 bg-white/50 p-8 text-center text-sm text-ink-500">
            No monthly spend data available yet—forward your receipts to get started.
          </div>
        )}
      </section>

      <section className="card p-6 space-y-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-ink-700">Product Price Trends</h2>
            <p className="text-sm text-ink-500">Compare the items with the richest purchase history to see how prices move.</p>
          </div>
        </div>
        {priceHistoryLoading ? (
          <div className="rounded-xl border border-dashed border-ink-200/80 bg-white/50 p-8 text-center text-sm text-ink-500">
            Loading price trends...
          </div>
        ) : (() => {
          const topProducts = getTopPriceTrendProducts(5);
          const priceChartData = formatPriceHistoryForChart(topProducts);

          return priceChartData.length > 0 ? (
            <div className="space-y-4">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={priceChartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="date"
                    type="number"
                    scale="time"
                    domain={['dataMin', 'dataMax']}
                    tickFormatter={(timestamp: number) =>
                      new Date(timestamp * 1000).toLocaleDateString('en-GB', {
                        month: 'short',
                        day: 'numeric',
                      })
                    }
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis label={{ value: 'Price (£)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip
                    formatter={(value: any) => `£${value.toFixed(2)}`}
                    labelFormatter={(label: any) => new Date(label * 1000).toLocaleDateString('en-GB')}
                  />
                  <Legend />
                  {topProducts.map((product, index) => {
                    const colors = ['#2563eb', '#f97316', '#10b981', '#6366f1', '#ec4899'];
                    return (
                      <Line
                        key={product.productName}
                        type="monotone"
                        dataKey={product.productName}
                        stroke={colors[index % colors.length]}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        isAnimationActive={false}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
              <p className="text-sm text-ink-500">
                Showing top {topProducts.length} products with price history containing at least two data points.
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-ink-200/80 bg-white/50 p-8 text-center text-sm text-ink-500">
              No product price trends available yet—grab multiple purchases of an item to unlock insights.
            </div>
          );
        })()}
      </section>
    </div>
  );
}
